export default function PrivacyPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12 text-slate-300">
      <h1 className="text-2xl font-semibold text-slate-200 mb-2">Privacy Policy</h1>
      <p className="text-xs text-slate-500 mb-8">Last updated: June 13, 2026</p>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">1. Information We Collect</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-2">
          We collect the following information when you use The Navigator:
        </p>
        <ul className="text-sm text-slate-400 list-disc list-inside space-y-1 ml-2">
          <li><span className="text-slate-300">Account information:</span> email address and authentication credentials</li>
          <li><span className="text-slate-300">Conversation data:</span> messages you send and AI responses within your sessions</li>
          <li><span className="text-slate-300">Progress data:</span> assignments, patterns, session summaries, and life thread organization</li>
          <li><span className="text-slate-300">Usage patterns:</span> session frequency, feature usage, and interaction data</li>
          <li><span className="text-slate-300">Billing information:</span> payment details processed by Stripe (we do not store card numbers)</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">2. How We Use Your Data</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-2">
          Your data is used to:
        </p>
        <ul className="text-sm text-slate-400 list-disc list-inside space-y-1 ml-2">
          <li>Provide persistent memory across sessions so The Navigator can track your progress</li>
          <li>Generate personalized assignments and accountability check-ins</li>
          <li>Identify behavioral patterns and provide cross-thread insights</li>
          <li>Process payments and manage your subscription</li>
          <li>Improve the Service (aggregated, de-identified data only)</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">3. Third-Party Services</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-2">
          We use the following third-party services to operate The Navigator:
        </p>
        <ul className="text-sm text-slate-400 list-disc list-inside space-y-1 ml-2">
          <li><span className="text-slate-300">Amazon Web Services (AWS):</span> hosting, data storage, and authentication</li>
          <li><span className="text-slate-300">Anthropic:</span> AI model provider for conversation processing</li>
          <li><span className="text-slate-300">Stripe:</span> payment processing for subscriptions</li>
          <li><span className="text-slate-300">Google Analytics:</span> aggregate website traffic and usage analytics</li>
        </ul>
        <p className="text-sm leading-relaxed text-slate-400 mt-2">
          Your conversation data is sent to Anthropic's API for processing. We do not
          sell your personal data to third parties.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">4. Data Retention</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          We retain your data for as long as your account is active. Conversation history,
          assignments, and patterns are stored to provide continuity across sessions.
          Upon account deletion, all personal data is permanently removed within 30 days.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">5. Your Rights</h2>
        <p className="text-sm leading-relaxed text-slate-400 mb-2">
          You have the right to:
        </p>
        <ul className="text-sm text-slate-400 list-disc list-inside space-y-1 ml-2">
          <li>Request a copy of your data</li>
          <li>Delete your account and all associated data</li>
          <li>Clear individual thread histories at any time</li>
          <li>Opt out of non-essential communications</li>
        </ul>
        <p className="text-sm leading-relaxed text-slate-400 mt-2">
          To exercise these rights, contact us at the email below.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">6. Cookies & Local Storage</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          We use essential cookies and browser local storage for authentication and session
          management. We also use Google Analytics (GA4), which sets its own cookies to
          measure aggregate site traffic and usage, such as pages visited, device and browser
          type, and approximate location derived from a truncated IP address. We do not use
          advertising or ad-retargeting pixels, and we do not sell this data. You can opt out
          with Google's{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Analytics opt-out add-on</a>{' '}
          or by controlling cookies in your browser settings. See{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Google's Privacy Policy</a>{' '}
          for how Google processes this data.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">7. Children's Privacy</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          The Navigator is not intended for children under 13 years of age. We do not
          knowingly collect personal information from children under 13. If we become
          aware that we have collected data from a child under 13, we will delete it
          promptly.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">8. Security</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          We implement industry-standard security measures including encryption in transit
          (TLS), encrypted storage, and secure authentication via AWS Cognito. However,
          no system is completely secure, and we cannot guarantee absolute data security.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">9. Changes to This Policy</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          We may update this Privacy Policy from time to time. Material changes will be
          communicated via email or in-app notification. Continued use of the Service
          after changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-200 mb-2 uppercase tracking-wide">10. Contact</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Questions about your privacy? Contact us at{' '}
          <a href="mailto:support@beelinemicrosystems.com" className="text-blue-400 hover:text-blue-300">
            support@beelinemicrosystems.com
          </a>.
        </p>
      </section>
    </div>
  );
}
